import { ADVANCED_NORMALIZATION_ERROR_MESSAGES } from "@/constants/message";
import { DatasetState } from "@/store/slice/datasetSlice";
import {
  Dataset,
  DatasetsMap,
  WeightValueEntry,
  WeightValues
} from "@/types/dataset";

import { getAvailableDatasets } from "./dataset";
import {
  getDatasetAttributeScoringId,
  getPoiWeightableAttributes,
  getPrimaryPoiAttribute,
  getValidNumericAttributes
} from "./datasetAttributes";

export type WeightPrimitive =
  | WeightValueEntry
  | string
  | number
  | undefined
  | null;

export type ZScoreValues = Record<
  string,
  Record<string, NonNullable<WeightValueEntry["z_score"]>>
>;

/**
 * Creates an independent snapshot of the advanced-normalization inputs keyed
 * by dataset and attribute, omitting entries with no z-score settings.
 */
export const getZScoreValues = (weightValues: WeightValues): ZScoreValues => {
  const zScoreValues: ZScoreValues = {};

  for (const [datasetId, dataset] of Object.entries(weightValues)) {
    const datasetZScores: ZScoreValues[string] = {};

    for (const [attributeId, attribute] of Object.entries(dataset.attributes)) {
      if (attribute.z_score) {
        datasetZScores[attributeId] = { ...attribute.z_score };
      }
    }

    if (Object.keys(datasetZScores).length > 0) {
      zScoreValues[datasetId] = datasetZScores;
    }
  }

  return zScoreValues;
};

export const normalizeWeightEntry = (
  entry: WeightValueEntry,
  defaultWeight = "0"
): WeightValueEntry => {
  if (!entry) return { weight: defaultWeight };

  if (typeof entry === "string") return { weight: entry };

  return {
    weight: entry.weight ?? defaultWeight,
    ...(entry.negative_impact !== undefined
      ? { negative_impact: entry.negative_impact }
      : {}),
    ...(entry.include_scoring !== undefined
      ? { include_scoring: entry.include_scoring }
      : {}),
    ...(entry.z_score !== undefined ? { z_score: { ...entry.z_score } } : {})
  };
};

export const getWeightString = (entry?: WeightPrimitive): string => {
  if (entry === null || entry === undefined) return "";
  if (typeof entry === "number") {
    return Number.isFinite(entry) ? entry.toString() : "";
  }
  if (typeof entry === "string") {
    return entry;
  }

  return entry.weight ?? "";
};

export const getNegativeImpactValue = (
  entry?: WeightPrimitive
): boolean | undefined => {
  if (!entry || typeof entry === "string" || typeof entry === "number") {
    return undefined;
  }
  return entry.negative_impact;
};

export const getIsIncludedScoring = (entry?: WeightPrimitive): boolean => {
  if (!entry || typeof entry === "string" || typeof entry === "number") {
    return true;
  }
  return entry.include_scoring !== false;
};

export const getPercentageValue = (
  entry?: WeightPrimitive
): string | undefined => {
  if (!entry || typeof entry === "string" || typeof entry === "number") {
    return undefined;
  }
  return entry.percentage;
};

export const parseWeightValue = (entry?: WeightPrimitive): number => {
  const value = getWeightString(entry);
  if (!value) return 0;
  const parsed = Number.parseFloat(value.replaceAll(",", ""));
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const hasNonZeroValue = (entry?: WeightPrimitive | null): boolean => {
  return parseWeightValue(entry) !== 0;
};

const hasNonEmptyString = (value?: string | null): boolean =>
  value !== undefined && value !== null && value.trim() !== "";

/**
 * The advanced-normalization stats for a weight entry, but ONLY when both
 * `log_mean` and `log_std_dev` are filled — a half-filled pair is treated as
 * "not set" (omit-when-empty rule). Returns the raw string form (edit model).
 */
export const getZScoreValue = (
  entry?: WeightPrimitive
): { log_mean: string; log_std_dev: string } | undefined => {
  if (!entry || typeof entry === "string" || typeof entry === "number") {
    return undefined;
  }
  const zScore = entry.z_score;
  if (
    !zScore ||
    !hasNonEmptyString(zScore.log_mean) ||
    !hasNonEmptyString(zScore.log_std_dev)
  ) {
    return undefined;
  }
  return zScore;
};

/**
 * The wire form (numbers) of a weight entry's advanced-normalization stats.
 * `undefined` when the pair is not fully set.
 */
export const parseZScore = (
  entry?: WeightPrimitive
): { log_mean: number; log_std_dev: number } | undefined => {
  const zScore = getZScoreValue(entry);
  if (!zScore) return undefined;
  return {
    log_mean: parseWeightValue(zScore.log_mean),
    log_std_dev: parseWeightValue(zScore.log_std_dev)
  };
};

/** Parse an advanced-normalization input the same way the wire payload does
 * (strip thousands separators) so validation and `parseZScore` agree. */
const parseLogValue = (value: string): number =>
  Number(value.trim().replaceAll(",", ""));

/**
 * Whether the Mean (`log_mean`) input is invalid: empty or not a finite number.
 * A negative log-mean is allowed (it can legitimately be negative).
 */
export const isLogMeanInvalid = (value: string): boolean => {
  if (value.trim() === "") return true;
  return !Number.isFinite(parseLogValue(value));
};

/**
 * Whether the Std-dev (`log_std_dev`) input is invalid: empty, not a finite
 * number, or `<= 0` (a standard deviation must be strictly positive).
 */
export const isLogStdDevInvalid = (value: string): boolean => {
  if (value.trim() === "") return true;
  const num = parseLogValue(value);
  return !Number.isFinite(num) || num <= 0;
};

export const getAdvancedNormalizationErrorMessage = ({
  logMean = "",
  logStdDev = ""
}: {
  logMean?: string;
  logStdDev?: string;
}): string | null => {
  const meanValue = logMean.trim();
  const stdDevValue = logStdDev.trim();
  const hasMean = meanValue !== "";
  const hasStdDev = stdDevValue !== "";

  if (hasMean && !Number.isFinite(parseLogValue(meanValue))) {
    return ADVANCED_NORMALIZATION_ERROR_MESSAGES.VALID_POSITIVE_NUMBER;
  }

  if (hasStdDev && !Number.isFinite(parseLogValue(stdDevValue))) {
    return ADVANCED_NORMALIZATION_ERROR_MESSAGES.VALID_POSITIVE_NUMBER;
  }

  if (!hasMean && !hasStdDev) {
    return ADVANCED_NORMALIZATION_ERROR_MESSAGES.BOTH_REQUIRED;
  }

  if (hasMean && !hasStdDev) {
    return ADVANCED_NORMALIZATION_ERROR_MESSAGES.STD_DEV_REQUIRED;
  }

  if (!hasMean && hasStdDev) {
    return ADVANCED_NORMALIZATION_ERROR_MESSAGES.MEAN_REQUIRED;
  }

  if (parseLogValue(stdDevValue) <= 0) {
    return ADVANCED_NORMALIZATION_ERROR_MESSAGES.STD_DEV_GREATER_THAN_ZERO;
  }

  return null;
};

/** Mean + stdv of a weight entry's `z_score`, defaulting to empty strings. */
const getZScoreInputs = (
  entry?: WeightPrimitive
): { log_mean: string; log_std_dev: string } => {
  if (!entry || typeof entry === "string" || typeof entry === "number") {
    return { log_mean: "", log_std_dev: "" };
  }
  return {
    log_mean: entry.z_score?.log_mean ?? "",
    log_std_dev: entry.z_score?.log_std_dev ?? ""
  };
};

const isZScoreEntryInvalid = (entry?: WeightPrimitive): boolean => {
  const { log_mean, log_std_dev } = getZScoreInputs(entry);
  return (
    getAdvancedNormalizationErrorMessage({
      logMean: log_mean,
      logStdDev: log_std_dev
    }) !== null
  );
};

/**
 * Whether any scorable attribute has an invalid advanced-normalization pair —
 * the signal that blocks Save in advanced mode. Mirrors the wire payload's
 * scorable selection: excluded datasets/attributes are skipped, and POI
 * datasets validate only their primary attribute (the one carrying the inputs).
 */
export const hasInvalidAdvancedNormalization = (
  localWeightValues: WeightValues,
  datasets: DatasetsMap,
  isPoiNumericAttributesEnabled = false
): boolean =>
  Object.entries(localWeightValues).some(([datasetId, dataset]) => {
    if (dataset.include_scoring === false) return false;
    const attributes = dataset.attributes || {};
    const sourceDataset = datasets[datasetId];
    // The edit model can retain an entry while dataset removal and local-weight
    // synchronization settle, or after a dataset becomes unavailable. It is
    // no longer rendered or scorable, so its stale normalization fields must
    // not block saving the remaining datasets.
    if (!sourceDataset?.data.available) return false;

    if (
      sourceDataset.properties.spatialType === "poi" &&
      !isPoiNumericAttributesEnabled
    ) {
      const primary = getPrimaryPoiAttribute(sourceDataset.categories);
      const primaryId = primary
        ? getDatasetAttributeScoringId(primary)
        : Object.keys(attributes)[0];
      if (!primaryId) return false;
      const entry = attributes[primaryId];
      return getIsIncludedScoring(entry) && isZScoreEntryInvalid(entry);
    }

    return Object.values(attributes).some(
      (entry) => getIsIncludedScoring(entry) && isZScoreEntryInvalid(entry)
    );
  });

export const calculateWeightPercentage = (
  datasetId: string,
  attributeIdx: string,
  totalWeight: number,
  localWeightValues: WeightValues
): string => {
  if (totalWeight === 0) return "0%";

  const datasetWeights = localWeightValues[datasetId];
  if (!datasetWeights) return "0%";

  const targetWeightEntry =
    attributeIdx === "weight"
      ? datasetWeights.weight
      : datasetWeights.attributes?.[attributeIdx];

  const weight = parseWeightValue(targetWeightEntry);

  const percentage = (weight / totalWeight) * 100;
  // Remove .00 if the percentage is a whole number
  const formattedPercentage = percentage.toFixed(2).replace(/\.00$/, "");
  return `${formattedPercentage}%`;
};

const NON_POI_SPATIAL_TYPES = ["polygon", "adm_area", "H3", "geohash", "line"];

/** Calculate default weight based on spatial type */
const getDefaultWeightForDataset = (
  dataset: Dataset,
  isPoiNumericAttributesEnabled: boolean
): number => {
  if (NON_POI_SPATIAL_TYPES.includes(dataset.properties.spatialType)) {
    return getValidNumericAttributes(dataset.categories).length;
  }
  if (dataset.properties.spatialType === "poi") {
    return isPoiNumericAttributesEnabled
      ? getPoiWeightableAttributes(dataset.categories).length
      : 1;
  }
  return dataset.categories.length;
};

/** Get valid attributes for a dataset based on its spatial type */
const getValidAttributesForDataset = (
  dataset: Dataset,
  isPoiNumericAttributesEnabled: boolean
) => {
  if (dataset.properties.spatialType === "poi") {
    return isPoiNumericAttributesEnabled
      ? getPoiWeightableAttributes(dataset.categories)
      : dataset.categories;
  }

  if (NON_POI_SPATIAL_TYPES.includes(dataset.properties.spatialType)) {
    return getValidNumericAttributes(dataset.categories);
  }

  return dataset.categories[0] ? [dataset.categories[0]] : [];
};

/** Calculate total weight across all datasets */
const calculateTotalDatasetWeight = (
  state: DatasetState,
  isPoiNumericAttributesEnabled: boolean
): number => {
  let total = 0;
  Object.entries(getAvailableDatasets(state.datasets)).forEach(
    ([datasetId, dataset]) => {
      if (state.weightValues[datasetId]?.include_scoring === false) {
        return;
      }
      let weight = parseWeightValue(state.weightValues[datasetId]?.weight);
      if (weight === 0 && !state.weightValues[datasetId]?.weight) {
        weight = getDefaultWeightForDataset(
          dataset,
          isPoiNumericAttributesEnabled
        );
      }
      total += weight;
    }
  );
  return total;
};

/** Calculate sum of attribute weights for a dataset */
const calculateAttributeWeightSum = (
  state: DatasetState,
  datasetId: string,
  validAttributes: {
    id: string;
    paired_key_attribute_id?: string | null;
  }[]
): number =>
  validAttributes.reduce((sum, attr) => {
    const attributeId = getDatasetAttributeScoringId(attr);
    const entry = state.weightValues[datasetId]?.attributes[attributeId];
    if (!getIsIncludedScoring(entry)) return sum;
    return sum + parseWeightValue(entry);
  }, 0);

/** Update percentage for a single attribute */
const updateAttributePercentage = (
  state: DatasetState,
  datasetId: string,
  attributeId: string,
  attributeWeightSum: number,
  datasetPercentage: string
) => {
  const datasetExcluded =
    state.weightValues[datasetId]?.include_scoring === false;
  const attrExcluded = !getIsIncludedScoring(
    state.weightValues[datasetId]?.attributes[attributeId]
  );

  let finalAttrPercentage: string;
  if (datasetExcluded || attrExcluded) {
    finalAttrPercentage = "0";
  } else {
    const attrPercentageWithinDataset = calculateWeightPercentage(
      datasetId,
      attributeId,
      attributeWeightSum,
      state.weightValues
    );

    const datasetPercentageValue = Number.parseFloat(datasetPercentage);
    finalAttrPercentage =
      attributeWeightSum === 0
        ? "0"
        : (
            (Number.parseFloat(attrPercentageWithinDataset) *
              datasetPercentageValue) /
            100
          )
            .toFixed(2)
            .replace(/\.00$/, "");
  }

  const currentAttr = state.weightValues[datasetId].attributes[attributeId];
  if (typeof currentAttr === "object" && currentAttr !== null) {
    currentAttr.percentage = finalAttrPercentage;
  } else {
    state.weightValues[datasetId].attributes[attributeId] = {
      weight: String(currentAttr || parseWeightValue(currentAttr)),
      percentage: finalAttrPercentage
    };
  }
};

/** Calculate the final percentage string for an attribute */
const calculateFinalAttrPercentage = (
  attrWeight: number,
  attrWeightSum: number,
  datasetPercentageStr: string
): string => {
  if (attrWeightSum === 0) return "0";
  const attrPercentageWithinDataset = (attrWeight / attrWeightSum) * 100;
  return (
    (attrPercentageWithinDataset * Number.parseFloat(datasetPercentageStr)) /
    100
  )
    .toFixed(2)
    .replace(/\.00$/, "");
};

/** Set percentage on an attribute weight entry, normalizing to WeightValueEntry if needed */
const updateAttrPercentage = (
  attributes: Record<string, WeightValueEntry>,
  attrId: string,
  attr: WeightPrimitive,
  percentage: string
) => {
  if (typeof attr === "object" && attr !== null) {
    attr.percentage = percentage;
  } else {
    attributes[attrId] = {
      weight: typeof attr === "string" ? attr : String(parseWeightValue(attr)),
      percentage
    };
  }
};

/** Calculate percentages from existing weight values without modifying the weights.
 * Used when loading a configuration where weights are already set by the backend
 * but percentages need to be computed client-side.
 *
 * Unlike performRecalculateAllPercentagesV2, this function:
 * - Does NOT modify existing weight values
 * - Does NOT require state.datasets (operates solely on state.weightValues)
 * - Only adds/updates the `percentage` field on each attribute's WeightValueEntry
 */
export const performCalculatePercentagesFromExistingWeights = (
  state: DatasetState
) => {
  const weightValues = Object.values(state.weightValues);

  // Calculate total dataset weight (excluding datasets opted out via include_scoring)
  const totalDatasetWeight = weightValues.reduce(
    (sum, entry) =>
      entry.include_scoring === false
        ? sum
        : sum + parseWeightValue(entry.weight),
    0
  );

  if (totalDatasetWeight === 0) return;

  for (const entry of weightValues) {
    if (entry.include_scoring === false) {
      // Force every attribute under an excluded dataset to "0"
      for (const [attrId, attr] of Object.entries(entry.attributes)) {
        updateAttrPercentage(entry.attributes, attrId, attr, "0");
      }
      continue;
    }

    const datasetWeight = parseWeightValue(entry.weight);
    const datasetPercentageStr = ((datasetWeight / totalDatasetWeight) * 100)
      .toFixed(2)
      .replace(/\.00$/, "");

    // Calculate sum of attribute weights within this dataset (skip excluded attrs)
    const attrWeightSum = Object.values(entry.attributes).reduce(
      (sum, attr) =>
        getIsIncludedScoring(attr) ? sum + parseWeightValue(attr) : sum,
      0
    );

    // Calculate and set percentage for each attribute
    for (const [attrId, attr] of Object.entries(entry.attributes)) {
      const finalAttrPercentage = !getIsIncludedScoring(attr)
        ? "0"
        : calculateFinalAttrPercentage(
            parseWeightValue(attr),
            attrWeightSum,
            datasetPercentageStr
          );
      updateAttrPercentage(entry.attributes, attrId, attr, finalAttrPercentage);
    }
  }
};

/** Helper function to recalculate all percentages
 * Extracted to allow calling from multiple reducers
 */
export const performRecalculateAllPercentagesV2 = (
  state: DatasetState,
  /** Defaults to the legacy single-component behaviour, so callers that predate
   * the POI numeric-attributes flag keep seeding POI datasets as before. */
  isPoiNumericAttributesEnabled = false
) => {
  const availableDatasets = getAvailableDatasets(state.datasets);
  const totalDatasetWeight = calculateTotalDatasetWeight(
    state,
    isPoiNumericAttributesEnabled
  );

  Object.entries(availableDatasets).forEach(([datasetId, dataset]) => {
    // Initialize weight values if needed
    if (!state.weightValues[datasetId]) {
      state.weightValues[datasetId] = { weight: "", attributes: {} };
    }
    if (!state.weightValues[datasetId].weight) {
      state.weightValues[datasetId].weight = getDefaultWeightForDataset(
        dataset,
        isPoiNumericAttributesEnabled
      ).toString();
    }

    const datasetPercentage = calculateWeightPercentage(
      datasetId,
      "weight",
      totalDatasetWeight,
      state.weightValues
    );

    // Sync weight with percentage by default
    const datasetPercentageValueStr = datasetPercentage.replace("%", "");
    state.weightValues[datasetId].weight = datasetPercentageValueStr;

    const validAttributes = getValidAttributesForDataset(
      dataset,
      isPoiNumericAttributesEnabled
    );
    const baseWeight = getDefaultWeightForDataset(
      dataset,
      isPoiNumericAttributesEnabled
    );

    // Initialize default weights for attributes
    const datasetPercentageValue = Number.parseFloat(datasetPercentageValueStr);
    const calculatedAttributeWeight =
      baseWeight === 0
        ? "100"
        : (datasetPercentageValue / baseWeight).toFixed(2).replace(/\.00$/, "");

    // Seed each attribute's weight so the dataset's share is distributed across
    // its attributes. POI and non-POI datasets distribute differently:
    //   - Non-POI: the dataset weight is split evenly, so every valid attribute
    //     gets `calculatedAttributeWeight`.
    //   - POI: scoring concentrates on the single primary attribute (lowest
    //     `position`). Only that attribute carries weight; all other POI
    //     attributes are pinned to "0" so they don't dilute the primary.
    // We must (re)seed here because attributes may be missing on first load or
    // hold stale weights after the dataset's percentage changed — downstream
    // attributeWeightSum / percentage calcs rely on these values being current.
    // Compute POI primary attribute once per dataset: it is loop-invariant.
    // Calling isPrimaryPoiScoringAttribute() per attribute re-ran an O(A) reduce
    // inside the O(A) loop, making POI datasets O(A²). Hoisting drops it to O(A).
    // With numeric attributes enabled a POI dataset seeds like any other:
    // every weightable component carries an even share and is included.
    const isPoiDataset =
      dataset.properties.spatialType === "poi" &&
      !isPoiNumericAttributesEnabled;
    const primaryPoiAttribute = isPoiDataset
      ? getPrimaryPoiAttribute(validAttributes)
      : undefined;

    validAttributes.forEach((attr) => {
      const attributeId = getDatasetAttributeScoringId(attr);
      const isPrimaryPoiAttribute =
        isPoiDataset && attr === primaryPoiAttribute;
      const isDatasetIncluded =
        state.weightValues[datasetId].include_scoring !== false;
      const attributeWeight =
        isPoiDataset && !isPrimaryPoiAttribute
          ? "0"
          : calculatedAttributeWeight;

      if (!state.weightValues[datasetId].attributes[attributeId]) {
        state.weightValues[datasetId].attributes[attributeId] = {
          weight: attributeWeight
        };
      } else {
        state.weightValues[datasetId].attributes[attributeId].weight =
          attributeWeight;
      }
      if (isPoiDataset) {
        // Only the primary POI attribute follows the dataset switch. Other POI
        // attributes are carried for metadata/filter restoration but excluded.
        state.weightValues[datasetId].attributes[attributeId].include_scoring =
          isPrimaryPoiAttribute && isDatasetIncluded;
      }
    });

    const attributeWeightSum = calculateAttributeWeightSum(
      state,
      datasetId,
      validAttributes
    );

    // Update percentages for each attribute
    validAttributes.forEach((attr) => {
      const attributeId = getDatasetAttributeScoringId(attr);
      updateAttributePercentage(
        state,
        datasetId,
        attributeId,
        attributeWeightSum,
        datasetPercentage
      );
    });
  });
};

/**
 * Dataset composition changes intentionally rebuild default weights. Preserve
 * only the advanced-normalization inputs for attributes that still exist after
 * that rebuild, so Mean and Stdv survive adding or removing another dataset.
 */
export const recalculateWeightValuesPreservingZScores = (
  state: DatasetState,
  isPoiNumericAttributesEnabled = false
) => {
  const zScoreValues = getZScoreValues(state.weightValues);

  state.weightValues = {};
  performRecalculateAllPercentagesV2(state, isPoiNumericAttributesEnabled);

  for (const [datasetId, datasetZScores] of Object.entries(zScoreValues)) {
    const dataset = state.weightValues[datasetId];
    if (!dataset) continue;

    for (const [attributeId, zScore] of Object.entries(datasetZScores)) {
      const attribute = dataset.attributes[attributeId];
      if (attribute) {
        attribute.z_score = zScore;
      }
    }
  }
};
